import type {IdentifiableListItem} from '@github-ui/shared-dashboard-lists/shared/helpers/item-identity'
import type {IssueListItem} from '@github-ui/shared-dashboard-lists/issues/types/list-types'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {useCallback} from 'react'

import {useRepoIssuesBulkActions} from './use-repo-issues-bulk-actions'

const MAX_SELECTABLE_MILESTONE_ROWS = 100

interface UseMilestoneIssuesBulkActionsArgs {
  owner: string
  repo: string
  repositoryId: string
  viewerId: string
  milestoneId: string
  view: 'open' | 'closed'
  rows: readonly IdentifiableListItem[]
  issueRows: IssueListItem[]
  viewerCanBulkUpdate: boolean
  hasProjectsEnabled: boolean
  issueTypesEnabled: boolean
  refreshIssues: () => Promise<{isError: boolean; error: unknown}>
}

export function useMilestoneIssuesBulkActions({
  owner,
  repo,
  repositoryId,
  viewerId,
  milestoneId,
  view,
  rows,
  issueRows,
  viewerCanBulkUpdate,
  hasProjectsEnabled,
  issueTypesEnabled,
  refreshIssues,
}: UseMilestoneIssuesBulkActionsArgs) {
  const bulkJobStorageKey = `issues.repo-bulk-job:v1:${viewerId}:${repositoryId}`
  const [bulkJobId, setBulkJobId, clearBulkJobId] = useLocalStorage<string | null>(bulkJobStorageKey, null, {
    validator: value => value === null || typeof value === 'string',
  })
  const onBulkJobStarted = useCallback((jobId: string) => setBulkJobId(jobId, {flush: true}), [setBulkJobId])
  const bulkActions = useRepoIssuesBulkActions({
    owner,
    repo,
    currentPage: 1,
    query: '',
    repositoryId,
    viewerCanBulkUpdate,
    totalCount: rows.length,
    issueRows,
    allRowsAreIssues: false,
    hasProjectsEnabled,
    issueTypesEnabled,
    refreshIssues,
    bulkJobPending: Boolean(bulkJobId),
    onBulkJobStarted,
    clearBulkJob: clearBulkJobId,
    selectionPolicy: {
      scopeKey: `${repositoryId}:${milestoneId}:${view}`,
      rows,
      maxLoadedRowsExclusive: MAX_SELECTABLE_MILESTONE_ROWS,
      updateIssuesTabCounter: false,
    },
  })

  return {bulkJobId, clearBulkJobId, ...bulkActions}
}
